import { Fragment, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useI18n } from '../../i18n';
import { saveRoundOverride } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { CheckIcon, ChevronDownIcon, DotIcon, LockIcon } from '../shared/Icons';
import { roundMajority, suggestPoints, type RoundOutcome } from '../../utils/roundSuggestion';
import type { Bout, JudgeCardRound, JudgeCardRow, JuryOverrideAction, JuryPair } from '../../types';

interface JuryScoringTableProps {
  bout: Bout;
  scores: JudgeCardRow[];
  rounds: number[];
  displayRounds: number[];
  overrides: Record<number, JuryPair>;
  savingRound: number | null;
  hasVerdict: boolean;
  canToggleDisplay: boolean;
  onDisplayToggle: (round: number, checked: boolean) => void;
  onOverride: (action: JuryOverrideAction) => void;
  /** The save state of the jury totals; a load of the scores reads it. */
  juryRounds: MutableRefObject<JuryRoundState>;
}

/**
 * #599 — the save state of the jury totals, which BoutCard owns. `unsaved`:
 * the rounds whose total the jury changed and the server does not hold yet.
 * `settled`: for each round, the `clock` value of its last successful save.
 * A load of the scores replaces no total of an unsaved round, and none of a
 * round whose save succeeded after the start of the load: the read of that
 * load can come before the write.
 */
export interface JuryRoundState { unsaved: Set<number>; settled: Map<number, number>; clock: number }

const MARK_CLASS = {
  C: 'bg-gray-100 text-gray-700 border-gray-300',
  W: 'bg-amber-100 text-amber-700 border-amber-300',
  K: 'bg-red-100 text-red-700 border-red-300',
} as const;

/** One letter for each caution (C), Warning (W) or knockdown (K) of a card. */
function Marks({ letter, count, title }: { letter: keyof typeof MARK_CLASS; count: number; title: string }) {
  if (!count) return null;
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} title={title}
          className={`inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-black border ${MARK_CLASS[letter]}`}>{letter}</span>
      ))}
    </>
  );
}

/** #599 — the suggestion of the cards of a round (an LMF rule after the
 *  five-judge rule of IFMA §29.4.6, utils/roundSuggestion.ts) and the
 *  majority of their round outcomes. An old card has no outcome: its higher
 *  number decides, as on the table. */
function roundSummary(scores: JudgeCardRow[], round: number) {
  const cards = scores.map(r => r.rounds[round]).filter((c): c is JudgeCardRound => !!c);
  const outcome = (c: JudgeCardRound): RoundOutcome =>
    c.round_outcome ?? (c.red_points > c.blue_points ? 'red' : c.blue_points > c.red_points ? 'blue' : 'even');
  return {
    red: suggestPoints(cards.map(c => c.red_points)),
    blue: suggestPoints(cards.map(c => c.blue_points)),
    count: cards.length,
    majority: roundMajority(cards.map(outcome)),
  };
}
type RoundSummary = ReturnType<typeof roundSummary>;

/**
 * The card of the jury: the cards of the judges, the majority, the total and
 * the approval of each round, in one table (operator, 2026-10-14). #599 had a
 * panel for each round; it used too much space. The arrows of a total add 1
 * or take 1, and a tick box approves a round. Before the verdict, the jury
 * can untick the last approved round to open it again.
 */
export default function JuryScoringTable({
  bout, scores, rounds, displayRounds, overrides, savingRound,
  hasVerdict, canToggleDisplay, onDisplayToggle, onOverride, juryRounds,
}: JuryScoringTableProps) {
  const { t } = useI18n();
  const { showError } = useToast();

  // #453 — the jury total of a round is a PAIR, saved only when the jury
  // CHANGED it. A focus and a blur alone saved the average of the cards as a
  // jury total, which then hid every later card, and on a round with no card
  // `|| 7` saved 7–7. An emptied field clears the pair, so the round goes back
  // to the suggestion of the cards. A pair whose other side has no value yet
  // waits for the jury: nothing is invented.
  const focusValue = useRef<number | null>(null);
  // The text of the field that the jury types in, until the blur. An emptied
  // field shows empty, not the suggestion, so the jury can type a new number.
  const [draft, setDraft] = useState<{ key: string; text: string } | null>(null);

  function editTotal(r: number, side: 'red' | 'blue', raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    // A letter alone changes nothing.
    if (digits === '' && raw.trim() !== '') return;
    // A load of the scores must not replace a total while the jury types it.
    juryRounds.current.unsaved.add(r);
    setDraft({ key: `${r}-${side}`, text: digits });
    onOverride({ round: r, key: side, value: digits === '' ? null : parseInt(digits) });
  }

  /** The round leaves `unsaved` when the blur saves nothing and no save waits. */
  function settleTyped(r: number) {
    if (!waiting.current[r] && !sending.current[r]) juryRounds.current.unsaved.delete(r);
  }

  function commitTotal(r: number, side: 'red' | 'blue', rt: RoundSummary) {
    const cur = overrides[r]?.[side] ?? null;
    if (cur === focusValue.current) { settleTyped(r); return; }
    const other: 'red' | 'blue' = side === 'red' ? 'blue' : 'red';
    if (cur === null) {
      onOverride({ round: r, key: other, value: null });
      saveNow(r, { red: null, blue: null });
      return;
    }
    // #469 — a jury total is the points after the Warnings: 4..10, as on the server.
    const v = Math.min(10, Math.max(4, cur));
    onOverride({ round: r, key: side, value: v });
    const o = overrides[r]?.[other] ?? rt[other];
    if (o === null) { settleTyped(r); return; }
    onOverride({ round: r, key: other, value: o });
    const [red, blue] = side === 'red' ? [v, o] : [o, v];
    saveNow(r, { red, blue });
  }

  // #599 — a press of an arrow saves the pair 500 ms after the last press:
  // one request for a quick series. A typed total saves at once. The saves of
  // a round go out one after the other, so a slow save cannot be stored after
  // a later one. The approval waits for them, and it approves only when the
  // last save succeeded: else the board shows an older total than the card.
  // A failed pair waits for the next approval. Leaving the page saves a
  // waiting pair too.
  //
  // The round stays in `juryRounds.unsaved` until the server holds the pair.
  // Without this state, a load of the scores (a new card, a resume) showed
  // the older total of the server. The next approval then sent a pair that
  // the jury no longer saw.
  const waiting = useRef<Record<number, JuryPair & { timer: number }>>({});
  const saves = useRef<Record<number, Promise<boolean>>>({});
  const sending = useRef<Record<number, number>>({});

  function saveLater(r: number, pair: JuryPair, delayMs: number) {
    juryRounds.current.unsaved.add(r);
    const prev = waiting.current[r];
    if (prev) window.clearTimeout(prev.timer);
    waiting.current[r] = { ...pair, timer: window.setTimeout(() => { void flush(r); }, delayMs) };
  }

  function saveNow(r: number, pair: JuryPair) {
    saveLater(r, pair, 0);
    void flush(r);
  }

  /** Sends the waiting pair of a round after the saves before it. True when the last save succeeded. */
  function flush(r: number): Promise<boolean> {
    const w = waiting.current[r];
    if (w) {
      window.clearTimeout(w.timer);
      delete waiting.current[r];
      sending.current[r] = (sending.current[r] ?? 0) + 1;
      const before = saves.current[r] ?? Promise.resolve(true);
      // A failed pair waits for the next approval with `timer: 0`, but only
      // when it is the newest pair of the round: no later save runs, and no
      // later pair waits. The saves of a round run in order. Before, a failed
      // older pair waited while a newer pair was on its way, and the next
      // approval sent the older pair over the newer pair of the card.
      saves.current[r] = before.then(() => saveRoundOverride(bout.id, r, w.red, w.blue).then(
        () => {
          juryRounds.current.settled.set(r, ++juryRounds.current.clock);
          return true;
        },
        err => {
          showError(err);
          if ((sending.current[r] ?? 0) <= 1 && !waiting.current[r]) waiting.current[r] = { red: w.red, blue: w.blue, timer: 0 };
          return false;
        },
      )).then(ok => {
        sending.current[r] -= 1;
        if (!sending.current[r] && !waiting.current[r]) juryRounds.current.unsaved.delete(r);
        return ok;
      });
    }
    return saves.current[r] ?? Promise.resolve(true);
  }
  useEffect(() => () => {
    for (const r of Object.keys(waiting.current)) void flush(Number(r));
  }, []);
  // A field that locks while it has the focus gets no blur (another device
  // approved the round): drop its text, so the round shows no old text when it
  // opens again, and let the loads refresh the round.
  useEffect(() => {
    if (!draft) return;
    const r = Number(draft.key.split('-')[0]);
    if (hasVerdict || displayRounds.includes(r)) { setDraft(null); settleTyped(r); }
  }, [draft, displayRounds, hasVerdict]);

  function step(r: number, side: 'red' | 'blue', delta: number, rt: RoundSummary) {
    const other: 'red' | 'blue' = side === 'red' ? 'blue' : 'red';
    // A round with no card and no jury total starts from 10, as a 10–10 round.
    const v = Math.min(10, Math.max(4, (overrides[r]?.[side] ?? rt[side] ?? 10) + delta));
    const o = overrides[r]?.[other] ?? rt[other] ?? 10;
    onOverride({ round: r, key: side, value: v });
    onOverride({ round: r, key: other, value: o });
    const [red, blue] = side === 'red' ? [v, o] : [o, v];
    saveLater(r, { red, blue }, 500);
  }

  // The round whose tick waits for the save of its total. The box shows the
  // tick at once and takes no second tap: a second tap sent a second approval.
  const [approving, setApproving] = useState<number | null>(null);

  async function approve(r: number) {
    setApproving(r);
    try {
      if (await flush(r)) onDisplayToggle(r, true);
    } finally {
      setApproving(null);
    }
  }

  const sideName = (side: RoundOutcome) => side === 'red' ? t.common.red : side === 'blue' ? t.common.blue : t.scoring.even;
  const summaries = Object.fromEntries(rounds.map(r => [r, roundSummary(scores, r)])) as Record<number, RoundSummary>;
  // The approved round with the highest number: only it can open again.
  const lastApproved = displayRounds.length ? Math.max(...displayRounds) : 0;
  // One approval at a time: a request for one round must not finish after a later request.
  const busy = savingRound !== null || approving !== null;
  // The card fits the width of a phone (operator, 2026-10-14): the table takes
  // the width of the card (`table-fixed`), and a bout of more than 3 rounds
  // uses smaller numbers on a phone. From `sm` up, the sizes are the same.
  // #651 — a total is only a little larger than a judge number (15 → 19 px,
  // 13 → 15 px): the number of a judge in a ring looked larger than the total.
  // The name column holds "Last First" (#661): it wraps (`wrap-anywhere`) and is 80 px
  // wide on a phone. A bout of more than 3 rounds had 64 px. At 96 px, the 3-round
  // table at 360 px leaves an arrow of the total under 40 px (side-judges-599).
  const fit = rounds.length > 3
    ? {
        firstCol: 'w-20 sm:w-24', name: 'text-[11px] sm:text-[13px]', head: 'text-[10px] sm:text-xs', num: 'text-[13px] sm:text-[15px]',
        numBox: 'min-w-6 px-0.5 leading-5 sm:min-w-7 sm:px-1 sm:leading-[22px]', total: 'text-[15px] sm:text-[19px]', foot: 'text-[10px] sm:text-xs',
        approve: 'gap-0.5 px-0 text-xs sm:gap-1.5 sm:px-2 sm:text-sm', box: 'w-4 h-4 sm:w-5 sm:h-5',
      }
    : {
        firstCol: 'w-20 sm:w-24', name: 'text-[13px]', head: 'text-xs', num: 'text-[15px]',
        numBox: 'min-w-7 px-1 leading-[22px]', total: 'text-[19px]', foot: 'text-xs',
        approve: 'gap-1.5 px-2 text-sm', box: 'w-5 h-5',
      };
  // The first column stays in view if a very narrow screen still scrolls.
  const firstCol = 'px-1 sm:px-1.5 sticky left-0 z-10 border-r text-center';

  return (
    // The "Jury" tag tells this card from the side judge's (operator, 2026-09-25).
    <div className="card overflow-hidden" data-testid="jury-card">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-base text-gray-900 flex items-center gap-2 whitespace-nowrap">{t.bout.card} <span className="badge-purple">{t.bout.jury}</span></h3>
      </div>
      {/* #531 — the cards show C (caution) markers too: the legend names all three. */}
      <p data-testid="jury-markers-legend" className="px-4 pt-2 pb-1 text-xs text-gray-500">{t.bout.juryMarkersLegend}</p>
      <div className="overflow-x-auto" data-testid="jury-table-scroll">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className={`bg-gray-50 ${fit.head} text-gray-500 uppercase tracking-wide`}>
              <th rowSpan={2} className={`${firstCol} py-2 font-medium bg-gray-50 border-gray-200 ${fit.firstCol}`}>{t.bout.judge}</th>
              {rounds.map(r => (
                <th key={r} colSpan={2} className="px-1 pt-2 text-center font-medium border-l border-gray-200">{`R${r}`}</th>
              ))}
            </tr>
            {/* The dots name the corner of each column: in an even round both numbers are grey. */}
            <tr className="bg-gray-50">
              {rounds.map(r => (
                <Fragment key={r}>
                  <th className="pb-1 border-l border-gray-200"><DotIcon className="w-3 h-3 mx-auto text-red-500" /></th>
                  <th className="pb-1"><DotIcon className="w-3 h-3 mx-auto text-blue-500" /></th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y divide-gray-100 ${fit.num}`}>
            {scores.map((row, i) => (
              <tr key={row.referee_id} data-judge={row.referee_id} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                <td title={row.referee_name}
                  className={`${firstCol} py-2 ${fit.name} wrap-anywhere text-gray-700 leading-tight border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>{row.referee_name}</td>
                {rounds.map(r => {
                  const rd = row.rounds[r];
                  // #469 — the judge's round outcome, as fought. A tinted box
                  // with a border of 1 px marks the winner (#651). This also
                  // applies when a Warning makes the points equal: 10–9 with a
                  // Warning to the winner is 9–9. A dotted grey box on both
                  // numbers marks an even round. An old card has no outcome:
                  // the higher number is colored, as before. Every number has
                  // the same box, so the two numbers of a round stay on one line.
                  const oc = rd?.round_outcome ?? null;
                  return (
                    <Fragment key={r}>
                      {(['red', 'blue'] as const).map(corner => {
                        const points = corner === 'red' ? rd?.red_points : rd?.blue_points;
                        const otherPoints = corner === 'red' ? rd?.blue_points : rd?.red_points;
                        const wins = oc ? oc === corner : (points ?? 0) > (otherPoints ?? 0);
                        const mark = oc === corner ? (corner === 'red' ? 'bg-red-50 border-red-300 text-red-700 font-bold' : 'bg-blue-50 border-blue-300 text-blue-700 font-bold')
                          : oc === 'even' ? 'bg-gray-100 border-dotted border-gray-400 text-gray-700 font-semibold'
                          : wins ? `border-transparent font-bold ${corner === 'red' ? 'text-red-600' : 'text-blue-600'}`
                          : 'border-transparent text-gray-600 font-medium';
                        const marks = {
                          C: (corner === 'red' ? rd?.red_cautions : rd?.blue_cautions) ?? 0,
                          W: (corner === 'red' ? rd?.red_warnings : rd?.blue_warnings) ?? 0,
                          K: (corner === 'red' ? rd?.red_knockdowns : rd?.blue_knockdowns) ?? 0,
                        };
                        return (
                          <td key={corner} data-round={r} data-corner={corner}
                            className={`px-0.5 py-2 text-center align-top ${corner === 'red' ? 'border-l border-gray-100' : ''}`}>
                            <div className="flex flex-col items-center gap-0.5">
                              <span data-card-ring={oc === corner || oc === 'even' ? oc : undefined}
                                className={`inline-block rounded-md border tabular-nums ${fit.numBox} ${mark}`}>
                                {points ?? '—'}
                              </span>
                              {(marks.C > 0 || marks.W > 0 || marks.K > 0) && (
                                <div className="flex flex-wrap justify-center gap-0.5 mt-0.5">
                                  <Marks letter="C" count={marks.C} title={t.bout.markCaution} />
                                  <Marks letter="W" count={marks.W} title={t.bout.markWarning} />
                                  <Marks letter="K" count={marks.K} title={t.bout.markKnockdown} />
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* #599 — the majority of the round outcomes of the cards. */}
            <tr className={`bg-gray-50 ${fit.foot} text-gray-500 border-t border-gray-200`}>
              <td className={`${firstCol} py-1.5 bg-gray-50 border-gray-200 uppercase font-semibold`}>{t.bout.majority}</td>
              {rounds.map(r => {
                const rs = summaries[r];
                const side = rs.majority.side;
                return (
                  <td key={r} colSpan={2} data-testid={`round-majority-${r}`} className="px-0.5 py-1.5 text-center border-l border-gray-200">
                    {rs.count === 0 ? '—'
                      : side ? <span className={`font-semibold ${side === 'red' ? 'text-red-600' : side === 'blue' ? 'text-blue-600' : 'text-gray-700'}`}>{`${sideName(side)} ${rs.majority.count}/${rs.majority.total}`}</span>
                      : t.bout.noMajority}
                  </td>
                );
              })}
            </tr>
            {/* The total of each round: the jury total, else the suggestion of the cards. */}
            <tr className="bg-gray-100 border-t-2 border-gray-300">
              <td className={`${firstCol} py-2 bg-gray-100 border-gray-200 ${fit.foot} font-bold text-gray-700 uppercase`}>{t.common.total}</td>
              {rounds.map(r => {
                const rs = summaries[r];
                const ov = overrides[r];
                // A round whose tick waits for its save takes no new total either.
                // Only the jury of the bout types a total: an admin sees the
                // table read-only, for oversight (#453; operator, 2026-10-14).
                const locked = !canToggleDisplay || hasVerdict || displayRounds.includes(r) || approving === r;
                return (
                  <Fragment key={r}>
                    {(['red', 'blue'] as const).map(side => {
                      const v = ov?.[side] ?? rs[side];
                      const color = side === 'red' ? 'text-red-600' : 'text-blue-600';
                      return (
                        <td key={side} className={`px-0 py-1 text-center ${side === 'red' ? 'border-l border-gray-200' : ''}`}>
                          {locked ? (
                            <span data-testid={`round-total-${r}-${side}`} className={`inline-block py-1 tabular-nums ${fit.total} font-extrabold ${color}`}>{v ?? '—'}</span>
                          ) : (
                            <div className="inline-flex flex-col w-full max-w-10">
                              {/* #627 — each arrow is 36 px high (h-9), the minimum touch target on a phone. */}
                              <button type="button" onClick={() => step(r, side, 1, rs)} disabled={v != null && v >= 10}
                                data-testid={`round-raise-${r}-${side}`}
                                aria-label={t.bout.raiseScore.replace('{side}', sideName(side))}
                                className="h-9 inline-flex items-center justify-center rounded-t-md bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40">
                                <ChevronDownIcon className="w-4 h-4 rotate-180" />
                              </button>
                              <input type="text" inputMode="numeric" maxLength={2}
                                value={draft?.key === `${r}-${side}` ? draft.text : (v ?? '')}
                                data-testid={`round-total-${r}-${side}`}
                                aria-label={`${t.bout.roundN.replace('{n}', String(r))} · ${sideName(side)}`}
                                onFocus={e => { focusValue.current = overrides[r]?.[side] ?? null; e.currentTarget.select(); }}
                                onChange={e => editTotal(r, side, e.target.value)}
                                onBlur={() => { setDraft(null); commitTotal(r, side, rs); }}
                                className={`h-9 w-full text-center tabular-nums ${fit.total} font-extrabold bg-white border-x border-gray-300 focus:outline-hidden focus:ring-2 focus:ring-inset ${color} ${side === 'red' ? 'focus:ring-red-400' : 'focus:ring-blue-400'}`} />
                              <button type="button" onClick={() => step(r, side, -1, rs)} disabled={v != null && v <= 4}
                                data-testid={`round-lower-${r}-${side}`}
                                aria-label={t.bout.lowerScore.replace('{side}', sideName(side))}
                                className="h-9 inline-flex items-center justify-center rounded-b-md bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40">
                                <ChevronDownIcon className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tr>
            {/* The approval of each round. Only the last approved round can
                be opened again, and only before the verdict. */}
            {(canToggleDisplay || hasVerdict) && (
              <tr className={hasVerdict ? 'bg-gray-50 border-t border-gray-200' : 'bg-amber-50 border-t border-amber-200'}>
                <td className={`${firstCol} py-1 ${fit.foot} font-semibold ${hasVerdict ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                  {/* #651 — after the verdict, the lock alone: with the word, the
                      label broke the line, and "Patvirtinta" is longer still. */}
                  {hasVerdict
                    ? <span role="img" title={t.bout.roundApproved} aria-label={t.bout.roundApproved} className="inline-flex"><LockIcon className="w-4 h-4" /></span>
                    : t.common.approve}
                </td>
                {rounds.map(r => {
                  const approved = displayRounds.includes(r) || approving === r;
                  const disabled = hasVerdict || !canToggleDisplay || busy || (approved && r !== lastApproved);
                  const tone = hasVerdict ? 'text-gray-500' : approved ? (disabled ? 'text-green-700/60' : 'text-green-800') : 'text-amber-800';
                  return (
                    <td key={r} colSpan={2} className={`px-1 py-0.5 text-center border-l ${hasVerdict ? 'border-gray-200' : 'border-amber-200'}`}>
                      <label className={`inline-flex items-center justify-center min-h-11 ${fit.approve} font-semibold ${tone} ${disabled ? '' : 'cursor-pointer'}`}>
                        <span className={`relative inline-flex ${fit.box}`}>
                          <input type="checkbox" data-testid={`round-approve-${r}`} checked={approved} disabled={disabled}
                            aria-label={`${t.common.approve} · ${t.bout.roundN.replace('{n}', String(r))}`}
                            onChange={e => {
                              if (e.target.checked) void approve(r);
                              else onDisplayToggle(r, false);
                            }}
                            className={`peer appearance-none ${fit.box} rounded border-2 bg-white ${hasVerdict ? 'border-gray-300 checked:bg-gray-200' : 'border-amber-500 checked:bg-green-600 checked:border-green-600 disabled:opacity-60'}`} />
                          <CheckIcon className={`pointer-events-none absolute inset-0 m-auto w-3.5 h-3.5 hidden peer-checked:block ${hasVerdict ? 'text-gray-500' : 'text-white'}`} />
                        </span>
                        {`R${r}`}
                      </label>
                    </td>
                  );
                })}
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}
